import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  Number,
  PrimaryFullScreenSpinner,
  SelectItem,
  SelectSeparator,
  StatusUI,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import {
  decimalSchema,
  formatLongUnitAmount,
  getCountryLists,
  NotFoundError,
} from "@repo/utils";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import Decimal from "decimal.js";
import countries from "i18n-iso-countries";
import { Suspense, useState } from "react";
import { z } from "zod";
import { PaymentIdentifiersCard } from "~/components/CustodyAccount/PaymentIdentifiers";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  CustodyAssetRowFragment,
  SetCustodyAccountAddressInput,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/custody-accounts_/$id")({
  head: () => opsHead("Custody Account"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const assetColumns: ColumnDef<CustodyAssetRowFragment>[] = [
  {
    id: "organisationName",
    accessorFn: (row) => row.businessEntity?.organisation?.name ?? "",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Organisation" />
    ),
    cell: ({ row }) => (
      <div className="flex flex-row items-center gap-2">
        <Icon
          imageUrl={
            row.original.businessEntity?.organisation?.squareLogoUrl ??
            undefined
          }
        />
        {row.original.businessEntity?.organisation?.name}
      </div>
    ),
  },
  {
    id: "businessEntityName",
    accessorFn: (row) => row.businessEntity?.name ?? "",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Business Entity" />
    ),
    cell: ({ row }) => row.original.businessEntity?.name ?? "-",
  },
  {
    id: "assetStatus",
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Asset Status" />
    ),
    cell: ({ row }) => <StatusUI status={row.original.status} />,
  },
  {
    id: "assetBalance",
    accessorFn: (row) => row.estimatedValue?.amount,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Asset Balance" />
    ),
    cell: ({ row }) => <CurrencyAmountUI {...row.original.estimatedValue} />,
  },
  {
    id: "assetOpenedAt",
    accessorFn: (row) => row.openedAt,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Asset Opened At" />
    ),
    cell: ({ row }) =>
      row.original.openedAt ? <DateUI date={row.original.openedAt} /> : "-",
  },
];

function View() {
  const { id } = Route.useParams();

  const { data, error } = useSuspenseQuery(
    graphql(`
      query AdminCustodyAccountDetail($id: ID!) {
        node(id: $id) {
          id
          ... on CustodyAccount {
            id
            shortRef
            currency
            product {
              id
              internalName
            }
            address {
              id
              buildingNumber
              streetName
              city
              state
              postcode
              country
            }
            ...CustodyAccountPaymentIdentifiers
            totalBalance {
              amount
              currency
            }
            assignedBalance {
              amount
              currency
            }
            unassignedBalance {
              amount
              currency
            }
            feeBalance {
              amount
              currency
            }
            totalUnitBalance {
              amount
              unit
            }
            clearingCustodyAccount {
              id
              shortRef
            }
            transactionFeeRate
            assets(includeInactive: true) {
              ...CustodyAssetRow
            }
          }
        }
      }

      fragment CustodyAssetRow on Asset {
        id
        openedAt
        status
        estimatedValue {
          currency
          amount
        }
        businessEntity {
          id
          name
          organisation {
            id
            name
            squareLogoUrl
          }
        }
      }
    `),
    {
      variables: { id },
    },
  );

  if (error) {
    throw error;
  }

  const custodyAccount =
    data.node?.__typename === "CustodyAccount" ? data.node : null;

  if (!custodyAccount) {
    throw new NotFoundError();
  }

  useOpsTitle(`Custody Account #${custodyAccount.shortRef}`);

  const [setAddress, setAddressResult] = useMutation(
    graphql(`
      mutation SetCustodyAccountAddress($input: SetCustodyAccountAddressInput!) {
        setCustodyAccountAddress(input: $input) {
          custodyAccount {
            id
            address {
              id
              buildingNumber
              streetName
              city
              state
              postcode
              country
            }
          }
        }
      }
    `),
    {
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Custody account address updated",
        });
      },
      onError: (error) => {
        const errorMessage =
          error.graphQLErrors?.[0]?.message || "Unexpected error";
        toast({
          kind: "error",
          autoHide: false,
          title: "Error setting address",
          description: errorMessage,
        });
      },
    },
  );

  const { commonCountriesList, uncommonCountriesList } = getCountryLists();

  const addressForm = useAppForm({
    defaultValues: {
      address: {
        buildingNumber: custodyAccount.address?.buildingNumber ?? "",
        streetName: custodyAccount.address?.streetName ?? "",
        city: custodyAccount.address?.city ?? "",
        state: custodyAccount.address?.state ?? "",
        postcode: custodyAccount.address?.postcode ?? "",
        country:
          [...commonCountriesList, ...uncommonCountriesList].find(
            ({ label }) => label === custodyAccount.address?.country,
          )?.value ?? "AUS",
      },
    } as Omit<SetCustodyAccountAddressInput, "custodyAccountId">,
    validators: {
      onSubmit: z.object({
        address: z.object({
          buildingNumber: z.string(),
          streetName: z.string(),
          city: z.string(),
          state: z.string(),
          postcode: z.string(),
          country: z.string().refine(countries.isValid),
        }),
      }),
    },
    onSubmit: async ({ value: formFields }) => {
      setAddress({
        variables: {
          input: {
            custodyAccountId: custodyAccount.id,
            address: formFields.address,
          },
        },
      });
    },
  });

  const [updateCustodyAccount] = useMutation(
    graphql(`
      mutation UpdateCustodyAccount($input: UpdateCustodyAccountInput!) {
        updateCustodyAccount(input: $input) {
          custodyAccount {
            id
            transactionFeeRate
          }
        }
      }
    `),
    {
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Custody account updated",
        });
      },
      onError: (error) => {
        const errorMessage =
          error.graphQLErrors?.[0]?.message || "Unexpected error";
        toast({
          kind: "error",
          autoHide: false,
          title: "Error updating custody account",
          description: errorMessage,
        });
      },
    },
  );

  const [editOpen, setEditOpen] = useState(false);

  const transactionFeeRateForm = useAppForm({
    defaultValues: {
      transactionFeeRate: custodyAccount.transactionFeeRate ?? new Decimal(0),
    },
    validators: {
      onSubmit: z.object({ transactionFeeRate: decimalSchema }),
    },
    onSubmit: async ({ value }) => {
      await updateCustodyAccount({
        variables: {
          input: {
            id: custodyAccount.id,
            transactionFeeRate: value.transactionFeeRate,
          },
        },
      });
      setEditOpen(false);
    },
  });

  const handleEditOpenChange = (open: boolean) => {
    if (!open) {
      transactionFeeRateForm.reset();
    }
    setEditOpen(open);
  };

  const navigate = useNavigate();

  const assets = [...(custodyAccount.assets ?? [])];

  return (
    <AdminPageContainer testId="admin-custody-account-detail">
      <AdminPageHeader
        title={`Custody Account #${custodyAccount.shortRef}`}
        subtitle={custodyAccount.id}
        buttons={
          <Link to="/custody-accounts">
            <Button variant="outline">Back to list</Button>
          </Link>
        }
      />

      <div className="grid grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Details</CardTitle>
            <Dialog open={editOpen} onOpenChange={handleEditOpenChange}>
              <DialogTrigger asChild>
                <Button variant="primary" size="sm">
                  Edit
                </Button>
              </DialogTrigger>
              <DialogContent requireIntentionalClosing={true}>
                <DialogHeader>
                  <DialogTitle>Edit Account Details</DialogTitle>
                </DialogHeader>
                <transactionFeeRateForm.AppForm>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      void transactionFeeRateForm.handleSubmit();
                    }}
                    className="space-y-4"
                  >
                    <transactionFeeRateForm.AppField name="transactionFeeRate">
                      {(field) => (
                        <field.PercentageField
                          label="Transaction Fee Rate"
                          scale="0-1"
                          decimalScale={3}
                        />
                      )}
                    </transactionFeeRateForm.AppField>
                    <div className="flex justify-end">
                      <transactionFeeRateForm.SubmitButton
                        childrenWhileSubmitting="Saving..."
                        disabled={
                          !transactionFeeRateForm.state.isValid ||
                          transactionFeeRateForm.state.isSubmitting
                        }
                      >
                        Save
                      </transactionFeeRateForm.SubmitButton>
                    </div>
                  </form>
                </transactionFeeRateForm.AppForm>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <div className="flex flex-row justify-between gap-2">
              <Txt variant="body" tone="tertiary">
                Product
              </Txt>
              <div className="text-right">
                <Txt variant="body">
                  {custodyAccount.product?.internalName ?? "-"}
                </Txt>
              </div>
            </div>
            <div className="flex flex-row justify-between gap-2">
              <Txt variant="body" tone="tertiary">
                Currency
              </Txt>
              <div className="text-right">
                <Txt variant="body">{custodyAccount.currency}</Txt>
              </div>
            </div>
            <div className="flex flex-row justify-between gap-2">
              <Txt variant="body" tone="tertiary">
                Total Balance
              </Txt>
              <Txt variant="body">
                <CurrencyAmountUI {...custodyAccount.totalBalance} />
              </Txt>
            </div>
            <div className="flex flex-row justify-between gap-2">
              <Txt variant="body" tone="tertiary">
                Total Unit Balance
              </Txt>
              <div className="text-right">
                <Txt variant="body">
                  {custodyAccount.totalUnitBalance
                    ? formatLongUnitAmount({
                        amount: custodyAccount.totalUnitBalance.amount,
                      })
                    : "-"}
                </Txt>
              </div>
            </div>
            <div className="flex flex-row justify-between gap-2">
              <Txt variant="body" tone="tertiary">
                Assigned Balance
              </Txt>
              <Txt variant="body">
                <CurrencyAmountUI {...custodyAccount.assignedBalance} />
              </Txt>
            </div>
            <div className="flex flex-row justify-between gap-2">
              <Txt variant="body" tone="tertiary">
                Unassigned Balance
              </Txt>
              <Txt variant="body">
                <CurrencyAmountUI {...custodyAccount.unassignedBalance} />
              </Txt>
            </div>
            {custodyAccount.transactionFeeRate && (
              <div className="flex flex-row justify-between gap-2">
                <Txt variant="body" tone="tertiary">
                  Fee Balance
                </Txt>
                <Txt variant="body">
                  <CurrencyAmountUI {...custodyAccount.feeBalance} />
                </Txt>
              </div>
            )}
            {custodyAccount.clearingCustodyAccount && (
              <div className="flex flex-row justify-between gap-2">
                <Txt variant="body" tone="tertiary">
                  Clearing Account
                </Txt>
                <Link
                  to="/custody-accounts/$id"
                  params={{ id: custodyAccount.clearingCustodyAccount.id }}
                  className="text-right text-info text-sm hover:underline"
                >
                  #{custodyAccount.clearingCustodyAccount.shortRef}
                </Link>
              </div>
            )}
            <div className="flex flex-row justify-between gap-2">
              <Txt variant="body" tone="tertiary">
                Transaction Fee Rate
              </Txt>
              <div className="text-right">
                <Txt variant="body">
                  {custodyAccount.transactionFeeRate ? (
                    <Number
                      value={custodyAccount.transactionFeeRate}
                      format={Number.format.percentage({
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 3,
                      })}
                    />
                  ) : (
                    "Not specified"
                  )}
                </Txt>
              </div>
            </div>
          </CardContent>
        </Card>

        <PaymentIdentifiersCard custodyAccount={custodyAccount} />
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Address</CardTitle>
        </CardHeader>
        <CardContent>
          <addressForm.AppForm>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void addressForm.handleSubmit();
              }}
            >
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-3 gap-4">
                  <addressForm.AppField name="address.buildingNumber">
                    {(field) => <field.InputField label="Building number" />}
                  </addressForm.AppField>
                  <addressForm.AppField name="address.streetName">
                    {(field) => <field.InputField label="Street name" />}
                  </addressForm.AppField>
                  <addressForm.AppField name="address.city">
                    {(field) => <field.InputField label="City" />}
                  </addressForm.AppField>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <addressForm.AppField name="address.state">
                    {(field) => <field.InputField label="State" />}
                  </addressForm.AppField>
                  <addressForm.AppField name="address.postcode">
                    {(field) => <field.InputField label="Postcode" />}
                  </addressForm.AppField>
                  <addressForm.AppField name="address.country">
                    {(field) => (
                      <field.SelectField
                        label="Country"
                        placeholder="Select a country"
                      >
                        {commonCountriesList.map(({ value, label }) => (
                          <SelectItem value={value} key={`common-${value}`}>
                            {label}
                          </SelectItem>
                        ))}
                        <SelectSeparator />
                        {uncommonCountriesList.map(({ value, label }) => (
                          <SelectItem value={value} key={`uncommon-${value}`}>
                            {label}
                          </SelectItem>
                        ))}
                      </field.SelectField>
                    )}
                  </addressForm.AppField>
                </div>

                <div className="flex justify-end">
                  <Button disabled={setAddressResult.loading}>Save</Button>
                </div>
              </div>
            </form>
          </addressForm.AppForm>
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Assets</CardTitle>
        </CardHeader>
        <CardContent>
          {assets.length === 0 ? (
            <Txt variant="body" tone="tertiary">
              No assets in this custody account
            </Txt>
          ) : (
            <DataTable
              data={assets}
              columns={assetColumns}
              className="overflow-hidden border"
              onRowClick={(row) => {
                void navigate({
                  to: "/assets/$id",
                  params: { id: row.original.id },
                });
              }}
            />
          )}
        </CardContent>
      </Card>
    </AdminPageContainer>
  );
}
